// Sites & Buildings page — Client → Site → Building hierarchy with full CRUD.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Plus, Pencil, Trash2, X, Save, Loader2, Building as BuildingIcon, MapPin,
    ChevronDown, ChevronRight, MoreVertical, Search, Sparkles, ScanLine, FolderOpen, ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import Pill from "@/components/Pill";
import { pillForRisk } from "@/lib/constants";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);
const SITE_TYPES = ["Civic", "Residential estate", "Mixed-use development", "Construction site", "Manufacturing", "Community", "Office / Commercial", "Other"];
const BUILDING_TYPES = ["High-rise residential", "Apartment block", "Mixed-use podium", "Public building", "Community centre", "Production facility", "Construction site", "Office", "Other"];
const STATE_OPTIONS = ["Enabled", "Linked", "Disabled", "N/A"];

export default function BuildingsPage() {
    const [clients, setClients] = useState([]);
    const [sites, setSites] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [q, setQ] = useState("");
    const [riskFilter, setRiskFilter] = useState("All");
    const [clientFilter, setClientFilter] = useState("All");
    const [sortBy, setSortBy] = useState("name");
    const [sortDir, setSortDir] = useState("asc");
    const [expanded, setExpanded] = useState({}); // {clientId: bool}
    const [siteDialog, setSiteDialog] = useState({ open: false, initial: null, clientId: null });
    const [allocSite, setAllocSite] = useState(null);   // site to allocate to a project
    const [buildingDialog, setBuildingDialog] = useState({ open: false, initial: null, clientId: null, siteId: null });
    const [siteDetail, setSiteDetail] = useState(null);       // read-only detail view — separate from edit dialogs
    const [buildingDetail, setBuildingDetail] = useState(null);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const canAskAi = user && user.role !== "site_manager";
    const askAi = (b) => navigate(`/app/ai-assist?context_type=building&context_id=${b.id}&context_label=${encodeURIComponent(b.name)}`);
    const openAsbestos = (b) => navigate(`/app/buildings/${b.id}/asbestos`);

    const refresh = useCallback(async () => {
        const params = portalClientId && !isInternalView ? { client_id: portalClientId } : {};
        const [c, s, b] = await Promise.all([
            api.get("/clients"),
            api.get("/sites", { params }),
            api.get("/buildings", { params: { ...params, ...(riskFilter !== "All" ? { risk: riskFilter } : {}) } }),
        ]);
        setClients(c.data); setSites(s.data); setBuildings(b.data);
        // Expand all clients by default on first load
        setExpanded((prev) => {
            if (Object.keys(prev).length) return prev;
            return Object.fromEntries(c.data.map((cl) => [cl.id, true]));
        });
    }, [portalClientId, isInternalView, riskFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit && clients.length) {
            const cid = (portalClientId && !isInternalView) ? portalClientId : clients[0]?.id;
            const cSites = sites.filter((s) => s.client_id === cid);
            setBuildingDialog({ open: true, initial: null, clientId: cid, siteId: cSites[0]?.id || null });
            searchParams.delete("new"); setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, clients, sites, portalClientId, isInternalView, setSearchParams]);

    useEffect(() => {
        const obId = searchParams.get("open_building");
        if (obId && buildings.length) {
            const b = buildings.find((x) => x.id === obId);
            if (b) {
                setExpanded((p) => ({ ...p, [b.client_id]: true }));
                if (canEdit) setBuildingDialog({ open: true, initial: b, clientId: b.client_id, siteId: b.site_id || null });
                else setQ(b.name);
            }
            searchParams.delete("open_building"); setSearchParams(searchParams, { replace: true });
        }
    }, [buildings, canEdit, searchParams, setSearchParams]);

    useEffect(() => {
        const osId = searchParams.get("open_site");
        if (osId && sites.length) {
            const s = sites.find((x) => x.id === osId);
            if (s) {
                setExpanded((p) => ({ ...p, [s.client_id]: true }));
                if (canEdit) setSiteDialog({ open: true, initial: s, clientId: s.client_id });
                else setQ(s.name);
            }
            searchParams.delete("open_site"); setSearchParams(searchParams, { replace: true });
        }
    }, [sites, canEdit, searchParams, setSearchParams]);

    const matchesSearch = (s) => !q || `${s.name} ${s.address || ""} ${s.site_type || ""} ${s.building_type || ""}`
        .toLowerCase().includes(q.toLowerCase());

    // Group: clients → sites → buildings (+ orphan buildings without a site_id)
    const scopedClients = (portalClientId && !isInternalView
        ? clients.filter((c) => c.id === portalClientId)
        : clients
    ).filter((c) => clientFilter === "All" || c.id === clientFilter);

    const sortBuildings = (rows) => {
        if (!sortBy) return rows;
        const accessors = {
            name: (b) => b.name || "",
            risk: (b) => ({ Low: 1, Medium: 2, High: 3 })[b.risk] || 0,
            bsc_state: (b) => b.bsc_state || "",
            height: (b) => b.height || 0,
            flats: (b) => b.flats || 0,
            building_type: (b) => b.building_type || "",
        };
        const acc = accessors[sortBy] || ((r) => r[sortBy] ?? "");
        const copy = [...rows];
        copy.sort((a, b) => {
            const av = acc(a); const bv = acc(b);
            const cmp = typeof av === "number" && typeof bv === "number"
                ? av - bv
                : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
            return sortDir === "asc" ? cmp : -cmp;
        });
        return copy;
    };

    async function archiveSite(s) {
        if (!window.confirm(`Archive site "${s.name}"? You'll need to reassign any buildings first.`)) return;
        try {
            await api.delete(`/sites/${s.id}`);
            toast.success(`Site archived: ${s.name}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not archive site");
        }
    }
    async function archiveBuilding(b) {
        if (!window.confirm(`Archive building "${b.name}"?`)) return;
        try {
            await api.delete(`/buildings/${b.id}`);
            toast.success(`Building archived: ${b.name}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not archive building");
        }
    }

    return (
        <div data-testid="buildings-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Clients & Portals
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Sites & Buildings</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {scopedClients.length} clients · {sites.length} sites · {buildings.length} buildings
                    </p>
                </div>
            </div>

            <div className="bg-white border rounded-[11px] shadow-brand-sm p-3 flex items-center gap-3 flex-wrap"
                 style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-1.5 flex-1 min-w-[220px] max-w-[340px]"
                     style={{ borderColor: "#E5E0DA" }}>
                    <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, address, type…"
                        className="flex-1 bg-transparent outline-none text-[12.5px]"
                        data-testid="buildings-search" />
                </div>
                <select value={riskFilter} onChange={(e) => setRiskFilter(e.target.value)}
                    data-testid="buildings-risk-filter"
                    className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none"
                    style={{ borderColor: "#E5E0DA" }}>
                    <option>All</option><option>Low</option><option>Medium</option><option>High</option>
                </select>
                {isInternalView && (
                    <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                        data-testid="buildings-client-filter"
                        className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none"
                        style={{ borderColor: "#E5E0DA" }}>
                        <option value="All">All clients</option>
                        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    data-testid="buildings-sort-by"
                    className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none"
                    style={{ borderColor: "#E5E0DA" }}>
                    <option value="name">Sort: Name</option>
                    <option value="risk">Sort: Risk</option>
                    <option value="bsc_state">Sort: BSC state</option>
                    <option value="height">Sort: Height</option>
                    <option value="flats">Sort: Flats</option>
                    <option value="building_type">Sort: Type</option>
                </select>
                <button onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="buildings-sort-dir"
                    className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none hover:bg-[#F5F3F0]"
                    style={{ borderColor: "#E5E0DA" }}>
                    {sortDir === "asc" ? "↑ Asc" : "↓ Desc"}
                </button>
                <div className="ml-auto text-[11.5px]" style={{ color: "#6F6A64" }}>
                    {buildings.length} building{buildings.length === 1 ? "" : "s"} in view
                </div>
            </div>

            <div className="space-y-4">
                {scopedClients.map((c) => {
                    const cSites = sites.filter((s) => s.client_id === c.id);
                    const cBuildings = sortBuildings(buildings.filter((b) => b.client_id === c.id));
                    const isOpen = expanded[c.id] ?? true;
                    return (
                        <div key={c.id} className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden"
                             style={{ borderColor: "#E5E0DA" }}>
                            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[#FCFBF9]"
                                 onClick={() => setExpanded((p) => ({ ...p, [c.id]: !isOpen }))}
                                 data-testid={`client-card-${c.slug}`}>
                                <span className="w-9 h-9 rounded-[8px] grid place-items-center text-[12px] font-bold text-white"
                                      style={{ background: c.branding.primary_colour }}>
                                    {c.branding.logo_initials}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold text-[14px] text-[#1F1F1F]">{c.name}</div>
                                    <div className="text-[11px]" style={{ color: "#6F6A64" }}>
                                        {cSites.length} site{cSites.length === 1 ? "" : "s"} · {cBuildings.length} building{cBuildings.length === 1 ? "" : "s"} · {c.sector || "—"}
                                    </div>
                                </div>
                                {canEdit && (
                                    <>
                                        <button data-testid={`new-site-${c.slug}`}
                                            onClick={(e) => { e.stopPropagation(); setSiteDialog({ open: true, initial: null, clientId: c.id }); }}
                                            className="text-[11.5px] font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] hover:bg-[#FBEFE5]"
                                            style={{ color: "#9F4212" }}>
                                            <Plus className="w-3 h-3" /> Site
                                        </button>
                                        <button data-testid={`new-building-${c.slug}`}
                                            onClick={(e) => { e.stopPropagation(); setBuildingDialog({ open: true, initial: null, clientId: c.id, siteId: cSites[0]?.id || null }); }}
                                            className="text-[11.5px] font-semibold inline-flex items-center gap-1 px-2.5 py-1.5 rounded-[7px] text-white"
                                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            <Plus className="w-3 h-3" /> Building
                                        </button>
                                    </>
                                )}
                                <span className="w-7 h-7 grid place-items-center" style={{ color: "#6F6A64" }}>
                                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </span>
                            </div>

                            {isOpen && (
                                <div className="border-t" style={{ borderColor: "#F0EDE8" }}>
                                    {cSites.length === 0 && cBuildings.length === 0 && (
                                        <div className="px-5 py-8 text-center italic text-[12.5px]" style={{ color: "#9A968F" }}>
                                            No sites or buildings yet. {canEdit ? "Use the buttons above to add the first one." : ""}
                                        </div>
                                    )}
                                    {cSites.filter(matchesSearch).map((s) => {
                                        const sBuildings = cBuildings.filter((b) => b.site_id === s.id && matchesSearch(b));
                                        return (
                                            <SiteBlock key={s.id} site={s} buildings={sBuildings}
                                                canEdit={canEdit} canAskAi={canAskAi} onAskAi={askAi}
                                                onAsbestos={openAsbestos}
                                                onOpenSite={() => setSiteDetail(s)}
                                                onOpenBuilding={(b) => setBuildingDetail(b)}
                                                onEditSite={() => setSiteDialog({ open: true, initial: s, clientId: c.id })}
                                                onArchiveSite={() => archiveSite(s)}
                                                onAllocate={() => setAllocSite({ site: s, clientId: c.id })}
                                                onEditBuilding={(b) => setBuildingDialog({ open: true, initial: b, clientId: c.id, siteId: s.id })}
                                                onArchiveBuilding={archiveBuilding}
                                                onNewBuilding={() => setBuildingDialog({ open: true, initial: null, clientId: c.id, siteId: s.id })} />
                                        );
                                    })}
                                    {/* Orphan buildings (no site_id) */}
                                    {(() => {
                                        const orphans = cBuildings.filter((b) => !b.site_id && matchesSearch(b));
                                        if (!orphans.length) return null;
                                        return (
                                            <SiteBlock site={{ id: "__orphans__", name: "Buildings not yet assigned to a site" }}
                                                buildings={orphans} canEdit={canEdit} isOrphan canAskAi={canAskAi} onAskAi={askAi}
                                                onAsbestos={openAsbestos}
                                                onOpenBuilding={(b) => setBuildingDetail(b)}
                                                onEditBuilding={(b) => setBuildingDialog({ open: true, initial: b, clientId: c.id, siteId: null })}
                                                onArchiveBuilding={archiveBuilding} />
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {siteDialog.open && (
                <SiteDialog initial={siteDialog.initial} clientId={siteDialog.clientId}
                    onClose={() => setSiteDialog({ open: false, initial: null, clientId: null })}
                    onSaved={refresh} />
            )}
            {buildingDialog.open && (
                <BuildingDialog initial={buildingDialog.initial} clientId={buildingDialog.clientId} siteId={buildingDialog.siteId}
                    sites={sites.filter((s) => s.client_id === buildingDialog.clientId)}
                    onClose={() => setBuildingDialog({ open: false, initial: null, clientId: null, siteId: null })}
                    onSaved={refresh} />
            )}
            {allocSite && (
                <AllocateSiteDialog site={allocSite.site} clientId={allocSite.clientId}
                    onClose={() => setAllocSite(null)} />
            )}
            {siteDetail && (
                <SiteDetailModal site={siteDetail} buildings={buildings.filter((b) => b.site_id === siteDetail.id)}
                    canEdit={canEdit} onClose={() => setSiteDetail(null)}
                    onEdit={() => { setSiteDialog({ open: true, initial: siteDetail, clientId: siteDetail.client_id }); setSiteDetail(null); }} />
            )}
            {buildingDetail && (
                <BuildingDetailModal building={buildingDetail}
                    site={sites.find((s) => s.id === buildingDetail.site_id) || null}
                    canEdit={canEdit} onClose={() => setBuildingDetail(null)}
                    onEdit={() => {
                        setBuildingDialog({ open: true, initial: buildingDetail, clientId: buildingDetail.client_id, siteId: buildingDetail.site_id || null });
                        setBuildingDetail(null);
                    }} />
            )}
        </div>
    );
}

function SiteBlock({ site, buildings, canEdit, isOrphan, canAskAi, onAskAi, onAsbestos, onOpenSite, onOpenBuilding, onEditSite, onArchiveSite, onAllocate, onEditBuilding, onArchiveBuilding, onNewBuilding }) {
    return (
        <div className="border-b last:border-b-0" style={{ borderColor: "#F0EDE8" }}>
            <div className="px-5 py-2.5 flex items-center gap-2 bg-[#F6F4F0]">
                {!isOrphan ? <MapPin className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} /> : null}
                <div className={`flex-1 min-w-0 ${!isOrphan ? "cursor-pointer hover:opacity-80" : ""}`}
                    data-testid={!isOrphan ? `site-open-${site.id}` : undefined}
                    onClick={!isOrphan ? () => onOpenSite?.() : undefined}>
                    <div className="text-[12px] font-semibold text-[#1F1F1F] flex items-center gap-1">
                        {site.name} {!isOrphan && <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />}
                    </div>
                    {site.site_type && <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{site.site_type}{site.address ? ` · ${site.address}` : ""}</div>}
                </div>
                {!isOrphan && canEdit && (
                    <>
                        <button data-testid={`site-edit-${site.id}`} onClick={onEditSite}
                            className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] hover:bg-white" style={{ color: "#6F6A64" }}>
                            <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button data-testid={`site-allocate-${site.id}`} onClick={onAllocate}
                            className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] hover:bg-white" style={{ color: "#9F4212" }}>
                            <MapPin className="w-3 h-3" /> Link to project
                        </button>
                        <button data-testid={`site-archive-${site.id}`} onClick={onArchiveSite}
                            className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] hover:bg-[#FBE8E5]" style={{ color: "#B33A2E" }}>
                            <Trash2 className="w-3 h-3" /> Archive
                        </button>
                        <button data-testid={`site-new-building-${site.id}`} onClick={onNewBuilding}
                            className="text-[10.5px] inline-flex items-center gap-1 px-2 py-1 rounded-[6px] text-white" style={{ background: "#1F1F1F" }}>
                            <Plus className="w-3 h-3" /> Building
                        </button>
                    </>
                )}
            </div>
            {buildings.length === 0 ? (
                <div className="px-5 py-4 italic text-[12px]" style={{ color: "#9A968F" }}>
                    No buildings under this site yet.
                </div>
            ) : (
                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-white text-[10px] uppercase tracking-wider border-b" style={{ color: "#9A968F", borderColor: "#F0EDE8" }}>
                            <th className="text-left px-5 py-2 font-bold">Building</th>
                            <th className="text-left px-3 py-2 font-bold">Type</th>
                            <th className="text-left px-3 py-2 font-bold">Units / storeys</th>
                            <th className="text-left px-3 py-2 font-bold">BSC</th>
                            <th className="text-left px-3 py-2 font-bold">Risk</th>
                            <th className="text-left px-3 py-2 font-bold">Next action</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {buildings.map((b) => (
                            <tr key={b.id} data-testid={`building-row-${b.id}`}
                                className="border-b hover:bg-[#FCFBF9]" style={{ borderColor: "#F6F4F0" }}>
                                <td className="px-5 py-3 cursor-pointer hover:opacity-80" data-testid={`building-open-${b.id}`}
                                    onClick={() => onOpenBuilding?.(b)}>
                                    <div className="flex items-center gap-2">
                                        <BuildingIcon className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                                        <div>
                                            <div className="font-semibold text-[#1F1F1F] flex items-center gap-1">
                                                {b.name} <ArrowUpRight className="w-3 h-3 text-[#C7C2BB]" />
                                            </div>
                                            {b.manager_name && <div className="text-[10px]" style={{ color: "#9A968F" }}>Manager: {b.manager_name}</div>}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-3 text-[#2D2D2D]">{b.building_type || "—"}</td>
                                <td className="px-3 py-3 text-[#2D2D2D] font-mono text-[11.5px]">{b.units} / {b.storeys}</td>
                                <td className="px-3 py-3"><Pill tone={b.bsc_state === "Enabled" ? "green" : b.bsc_state === "Linked" ? "blue" : "neutral"}>{b.bsc_state}</Pill></td>
                                <td className="px-3 py-3"><Pill tone={pillForRisk(b.risk)}>{b.risk}</Pill></td>
                                <td className="px-3 py-3 text-[11.5px]" style={{ color: "#6F6A64" }}>{b.next_action || "—"}</td>
                                <td className="px-3 py-3 text-right">
                                    <div className="inline-flex gap-1">
                                        {onAsbestos && (
                                            <button data-testid={`building-asbestos-${b.id}`} onClick={() => onAsbestos(b)}
                                                className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBEDE6]" title="Asbestos floorplans">
                                                <ScanLine className="w-3.5 h-3.5" style={{ color: "#C8632A" }} />
                                            </button>
                                        )}
                                        {canAskAi && (
                                            <button data-testid={`building-ask-ai-${b.id}`} onClick={() => onAskAi(b)}
                                                className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#EEF3FB]" title="Ask Beyond AI about this building">
                                                <Sparkles className="w-3.5 h-3.5" style={{ color: "#1F6FE5" }} />
                                            </button>
                                        )}
                                        {canEdit && (
                                            <>
                                                <button data-testid={`building-edit-${b.id}`} onClick={() => onEditBuilding(b)}
                                                    className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#F6F4F0]" title="Edit">
                                                    <Pencil className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                                                </button>
                                                <button data-testid={`building-archive-${b.id}`} onClick={() => onArchiveBuilding(b)}
                                                    className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBE8E5]" title="Archive">
                                                    <Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function SiteDialog({ initial, clientId, onClose, onSaved }) {
    const [form, setForm] = useState(initial || { name: "", site_type: "", address: "", notes: "" });
    const [busy, setBusy] = useState(false);
    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function save() {
        if (!form.name?.trim()) { toast.error("Site name is required"); return; }
        const cleaned = Object.fromEntries(Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === "")));
        setBusy(true);
        try {
            if (initial) {
                const { data } = await api.patch(`/sites/${initial.id}`, cleaned);
                toast.success(`Updated site: ${data.name}`);
            } else {
                const { data } = await api.post("/sites", { ...cleaned, client_id: clientId });
                toast.success(`Created site: ${data.name}`);
            }
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    }

    return (
        <Modal title={initial ? "Edit site" : "New site"} subtitle="Sites are the physical / civic locations under a client (e.g. an estate or a campus)."
               icon={<MapPin className="w-5 h-5" />} onClose={onClose} onSave={save} busy={busy}
               testId="site-form-dialog">
            <FormField label="Site name" required>
                <input data-testid="site-form-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="fi" />
            </FormField>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Site type">
                    <select data-testid="site-form-type" value={form.site_type || ""} onChange={(e) => set("site_type", e.target.value)} className="fi">
                        <option value="">Select…</option>
                        {SITE_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                </FormField>
                <FormField label="Address">
                    <input data-testid="site-form-address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="fi" />
                </FormField>
            </div>
            <FormField label="Notes">
                <textarea data-testid="site-form-notes" rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} className="fi" />
            </FormField>
        </Modal>
    );
}

function BuildingDialog({ initial, clientId, siteId, sites, onClose, onSaved }) {
    const [form, setForm] = useState(initial || {
        name: "", building_type: "", units: 0, storeys: 0, areas: "",
        bsc_state: "N/A", golden_state: "N/A", risk: "Medium",
        manager_name: "", next_action: "", site_id: siteId || "",
    });
    const [stairwellsText, setStairwellsText] = useState((initial?.stairwells || []).join(", "));
    const [busy, setBusy] = useState(false);
    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    async function save() {
        if (!form.name?.trim()) { toast.error("Building name is required"); return; }
        const cleaned = Object.fromEntries(Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === "")));
        if (cleaned.units != null && cleaned.units !== "") cleaned.units = Number(cleaned.units);
        if (cleaned.storeys != null && cleaned.storeys !== "") cleaned.storeys = Number(cleaned.storeys);
        cleaned.stairwells = stairwellsText.split(",").map((s) => s.trim()).filter(Boolean);
        setBusy(true);
        try {
            if (initial) {
                const { data } = await api.patch(`/buildings/${initial.id}`, cleaned);
                toast.success(`Updated: ${data.name}`);
            } else {
                const { data } = await api.post("/buildings", { ...cleaned, client_id: clientId });
                toast.success(`Created: ${data.name}`);
            }
            onSaved?.(); onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally { setBusy(false); }
    }

    return (
        <Modal title={initial ? "Edit building" : "New building"} subtitle="Buildings live under a site. They drive the BSC and Golden Thread workflows."
               icon={<BuildingIcon className="w-5 h-5" />} onClose={onClose} onSave={save} busy={busy}
               testId="building-form-dialog">
            <FormField label="Building name" required>
                <input data-testid="building-form-name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className="fi" />
            </FormField>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Site">
                    <select data-testid="building-form-site" value={form.site_id || ""} onChange={(e) => set("site_id", e.target.value)} className="fi">
                        <option value="">— No site yet —</option>
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                </FormField>
                <FormField label="Building type">
                    <select data-testid="building-form-type" value={form.building_type || ""} onChange={(e) => set("building_type", e.target.value)} className="fi">
                        <option value="">Select…</option>
                        {BUILDING_TYPES.map((t) => <option key={t}>{t}</option>)}
                    </select>
                </FormField>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Units"><input data-testid="building-form-units" type="number" min={0} value={form.units ?? 0} onChange={(e) => set("units", e.target.value)} className="fi" /></FormField>
                <FormField label="Storeys"><input data-testid="building-form-storeys" type="number" min={0} value={form.storeys ?? 0} onChange={(e) => set("storeys", e.target.value)} className="fi" /></FormField>
            </div>
            <FormField label="Areas / scope">
                <textarea data-testid="building-form-areas" rows={2} value={form.areas || ""} onChange={(e) => set("areas", e.target.value)} className="fi" placeholder="e.g. 42 floors / 2 stairs / plant rooms" />
            </FormField>
            <div className="grid sm:grid-cols-3 gap-3">
                <FormField label="BSC state">
                    <select data-testid="building-form-bsc" value={form.bsc_state || "N/A"} onChange={(e) => set("bsc_state", e.target.value)} className="fi">
                        {STATE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                </FormField>
                <FormField label="Golden Thread">
                    <select data-testid="building-form-gt" value={form.golden_state || "N/A"} onChange={(e) => set("golden_state", e.target.value)} className="fi">
                        {STATE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                </FormField>
                <FormField label="Risk">
                    <select data-testid="building-form-risk" value={form.risk || "Medium"} onChange={(e) => set("risk", e.target.value)} className="fi">
                        <option>Low</option><option>Medium</option><option>High</option>
                    </select>
                </FormField>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <FormField label="Building manager">
                    <input data-testid="building-form-manager" value={form.manager_name || ""} onChange={(e) => set("manager_name", e.target.value)} className="fi" />
                </FormField>
                <FormField label="Next action">
                    <input data-testid="building-form-next" value={form.next_action || ""} onChange={(e) => set("next_action", e.target.value)} className="fi" placeholder="e.g. BSC evidence walk · 2026-06-11" />
                </FormField>
            </div>
            <FormField label="Stairwells">
                <input data-testid="building-form-stairwells" value={stairwellsText} onChange={(e) => setStairwellsText(e.target.value)} className="fi"
                    placeholder="e.g. Yellow Stair 1 (North), Blue Stair 2 (South)" />
                <p className="text-[11px] mt-1" style={{ color: "#9A968F" }}>Comma-separated — options for the per-row Stairwell column on the Weekly Fire Check.</p>
            </FormField>
        </Modal>
    );
}

function AllocateSiteDialog({ site, clientId, onClose }) {
    const [projects, setProjects] = useState([]);
    const [projectId, setProjectId] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/projects", { params: { client_id: clientId } })
            .then((r) => setProjects((r.data || []).filter((p) => p.status !== "Cancelled")))
            .catch(() => setProjects([]));
    }, [clientId]);

    const alreadyLinked = (p) => (p.site_ids || (p.site_id ? [p.site_id] : [])).includes(site.id);

    async function save() {
        if (!projectId) { toast.error("Choose a project"); return; }
        const proj = projects.find((p) => p.id === projectId);
        const ids = proj.site_ids || (proj.site_id ? [proj.site_id] : []);
        if (ids.includes(site.id)) { toast.info("Site already linked to this project"); onClose(); return; }
        setBusy(true);
        try {
            await api.patch(`/projects/${projectId}`, { site_ids: [...ids, site.id] });
            toast.success(`Linked "${site.name}" to ${proj.name}`);
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not link to project");
        } finally { setBusy(false); }
    }

    return (
        <Modal title="Link site to a project" subtitle={`Allocate "${site.name}" to a project so its inspections and forms can be scoped to it.`}
               icon={<MapPin className="w-5 h-5" />} onClose={onClose} onSave={save} busy={busy}
               testId="allocate-site-dialog">
            <FormField label="Project" required>
                <select data-testid="allocate-site-project" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="fi">
                    <option value="">Select a project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.reference ? ` (${p.reference})` : ""}{alreadyLinked(p) ? " — already linked" : ""}</option>)}
                </select>
            </FormField>
        </Modal>
    );
}

function useLinkedProjects(clientId, siteId) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (!clientId || !siteId) { setProjects([]); setLoading(false); return; }
        setLoading(true);
        api.get("/projects", { params: { client_id: clientId } })
            .then((r) => {
                const linked = (r.data || []).filter((p) => (p.site_ids || (p.site_id ? [p.site_id] : [])).includes(siteId));
                setProjects(linked);
            })
            .catch(() => setProjects([]))
            .finally(() => setLoading(false));
    }, [clientId, siteId]);
    return { projects, loading };
}

function DetailRow({ label, value }) {
    return (
        <div>
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{label}</span>
            <div className="text-[13px] text-[#1F1F1F] whitespace-pre-wrap">{value}</div>
        </div>
    );
}

function LinkedProjectsList({ testPrefix, projects, loading, emptyMsg }) {
    const navigate = useNavigate();
    return (
        <div>
            <span className="text-[11px] font-semibold mb-1.5 flex items-center gap-1" style={{ color: "#6F6A64" }}>
                <FolderOpen className="w-3 h-3" /> Linked projects
            </span>
            {loading ? (
                <p className="text-[12px] italic" style={{ color: "#9A968F" }}>Loading…</p>
            ) : projects.length === 0 ? (
                <p className="text-[12px] italic" style={{ color: "#9A968F" }}>{emptyMsg}</p>
            ) : (
                <ul className="space-y-1">
                    {projects.map((p) => (
                        <li key={p.id} data-testid={`${testPrefix}-project-${p.id}`}>
                            <button onClick={() => navigate(`/app/projects/${p.id}`)}
                                className="text-[12.5px] font-semibold hover:underline inline-flex items-center gap-1" style={{ color: "#9F4212" }}>
                                {p.name}{p.reference ? ` (${p.reference})` : ""} <ArrowUpRight className="w-3 h-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function SiteDetailModal({ site, buildings, canEdit, onClose, onEdit }) {
    const { projects, loading } = useLinkedProjects(site.client_id, site.id);
    return (
        <Modal title={site.name} subtitle="Site details" icon={<MapPin className="w-5 h-5" />}
            onClose={onClose} testId="site-detail-modal" readOnly
            footerExtra={canEdit && (
                <button data-testid="site-detail-edit" onClick={onEdit}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
            )}>
            <div className="grid sm:grid-cols-2 gap-3">
                <DetailRow label="Site type" value={site.site_type || "—"} />
                <DetailRow label="Address" value={site.address || "—"} />
            </div>
            <DetailRow label="Notes" value={site.notes || "—"} />
            <div>
                <span className="text-[11px] font-semibold mb-1.5 block" style={{ color: "#6F6A64" }}>Buildings under this site ({buildings.length})</span>
                {buildings.length === 0 ? (
                    <p className="text-[12px] italic" style={{ color: "#9A968F" }}>No buildings linked yet.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {buildings.map((b) => (
                            <li key={b.id} data-testid={`site-detail-building-${b.id}`} className="text-[12.5px] text-[#1F1F1F] flex items-center gap-1.5">
                                <BuildingIcon className="w-3 h-3" style={{ color: "#9A968F" }} /> {b.name}
                                <Pill tone={pillForRisk(b.risk)}>{b.risk}</Pill>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            <LinkedProjectsList testPrefix="site-detail" projects={projects} loading={loading}
                emptyMsg="No projects linked to this site yet." />
        </Modal>
    );
}

function BuildingDetailModal({ building: b, site, canEdit, onClose, onEdit }) {
    const { projects, loading } = useLinkedProjects(b.client_id, b.site_id);
    return (
        <Modal title={b.name} subtitle="Building details" icon={<BuildingIcon className="w-5 h-5" />}
            onClose={onClose} testId="building-detail-modal" readOnly
            footerExtra={canEdit && (
                <button data-testid="building-detail-edit" onClick={onEdit}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
            )}>
            <div className="grid sm:grid-cols-2 gap-3">
                <DetailRow label="Building type" value={b.building_type || "—"} />
                <DetailRow label="Site" value={site ? site.name : "Not yet assigned"} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <DetailRow label="Units" value={b.units ?? 0} />
                <DetailRow label="Storeys" value={b.storeys ?? 0} />
            </div>
            <DetailRow label="Areas / scope" value={b.areas || "—"} />
            <div className="grid sm:grid-cols-3 gap-3">
                <DetailRow label="BSC state" value={b.bsc_state || "N/A"} />
                <DetailRow label="Golden Thread" value={b.golden_state || "N/A"} />
                <DetailRow label="Risk" value={<Pill tone={pillForRisk(b.risk)}>{b.risk}</Pill>} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <DetailRow label="Building manager" value={b.manager_name || "—"} />
                <DetailRow label="Next action" value={b.next_action || "—"} />
            </div>
            {site ? (
                <LinkedProjectsList testPrefix="building-detail" projects={projects} loading={loading}
                    emptyMsg="No projects linked to this site yet." />
            ) : (
                <p className="text-[12px] italic" style={{ color: "#9A968F" }}>Assign this building to a site to see linked projects.</p>
            )}
        </Modal>
    );
}

function FormField({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}

function Modal({ title, subtitle, icon, onClose, onSave, busy, testId, children, readOnly, footerExtra }) {
    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in"
             style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid={testId}
                className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[680px] max-h-[92vh] flex flex-col border"
                style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center"
                         style={{ background: "#EEE9F6", color: "#6B4E9E" }}>{icon}</div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">{title}</h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>{subtitle}</p>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"
                        data-testid={`${testId}-close`}>
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane3">{children}</div>
                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid={`${testId}-cancel`}
                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white"
                        style={{ borderColor: "#E5E0DA" }}>{readOnly ? "Close" : "Cancel"}</button>
                    {readOnly ? footerExtra : (
                        <button onClick={onSave} disabled={busy} data-testid={`${testId}-save`}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                            {busy ? "Saving…" : "Save"}
                        </button>
                    )}
                </div>
                <style>{`
                    .form-pane3 .fi {
                        width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA;
                        border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none;
                        transition: border-color .12s, box-shadow .12s;
                    }
                    .form-pane3 .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}
